import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { Route } from '../../models/route';
import { useT } from '../../i18n';

interface RouteChecklistProps {
  routes: Route[];
  selected: string[];
  selectAll: boolean;
  onToggle: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onToggleAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  /** La sede ya está elegida: si no tiene rutas se dice, en vez de dejar el recuadro vacío. */
  officeChosen?: boolean;
}

/**
 * Rutas de una sede para marcar: recuadro con «Todas las rutas» arriba y las rutas en columnas que se
 * acomodan al ancho (una en teléfono). Lo usan los filtros de informes y la asignación de rutas.
 */
const RouteChecklist: React.FC<RouteChecklistProps> = ({ routes, selected, selectAll, onToggle, onToggleAll, disabled = false, officeChosen = true }) => {
  const t = useT();
  const count = routes.filter((r) => selected.includes(String(r.id))).length;
  return (
    <Box
      role="group"
      aria-label={t('filter.routes')}
      aria-disabled={disabled || undefined}
      sx={{ minWidth: 0, border: 1, borderColor: 'divider', borderRadius: 1, opacity: disabled ? 0.6 : 1 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          columnGap: 2,
          px: 1.5,
          py: 0.5,
          borderBottom: routes.length ? 1 : 0,
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t('filter.routes')}
          {routes.length > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {t('filter.selectedCount', { count, total: routes.length })}
            </Typography>
          )}
        </Typography>
        {routes.length > 0 && (
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Checkbox
                size="small"
                checked={selectAll}
                onChange={onToggleAll}
                disabled={disabled}
                indeterminate={!disabled && count > 0 && count < routes.length}
              />
            }
            label={<Typography variant="body2">{t('filter.selectAll')}</Typography>}
          />
        )}
      </Box>
      {routes.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))',
            columnGap: 1,
            px: 1.5,
            py: 0.5,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          {routes.map((route) => (
            <FormControlLabel
              key={route.id}
              sx={{ m: 0, minWidth: 0, '& .MuiFormControlLabel-label': { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
              control={
                <Checkbox size="small" checked={selected.includes(String(route.id))} onChange={onToggle} value={String(route.id)} disabled={disabled} />
              }
              label={<Typography variant="body2" noWrap title={route.name}>{route.name}</Typography>}
            />
          ))}
        </Box>
      ) : (
        officeChosen && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
            {t('filter.noRoutes')}
          </Typography>
        )
      )}
    </Box>
  );
};

export default RouteChecklist;
